import React, {useEffect, useRef, useState} from 'react';
import {adminLearnerStateApi, getErrorMessage} from '../services/api';

const WIDTH = 760;
const HEIGHT = 240;
const LEFT = 42;
const RIGHT = 16;
const TOP = 18;
const BOTTOM = 35;
const chartWidth = WIDTH - LEFT - RIGHT;
const chartHeight = HEIGHT - TOP - BOTTOM;
const ratio = (value, status) => {
    if (value == null || status === 'unassessed') return null;
    const n = Number(value);
    return Number.isFinite(n) && n >= 0 && n <= 1 ? n : null;
};
const percent = (value, status) => ratio(value, status) === null
    ? 'هنوز ارزیابی نشده' : `${Math.round(Number(value) * 100).toLocaleString('fa-IR')}٪`;
const dateText = (value) => {
    if (!value) return 'نامشخص';
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? 'نامشخص' : new Intl.DateTimeFormat('fa-IR', {
        dateStyle: 'medium', timeStyle: 'short',
    }).format(date);
};
const segmentKey = (point, graphId, index) => {
    if (point.algorithm_version == null || (graphId != null && point.resource_version == null)) return `unknown:${index}`;
    return `${point.algorithm_version}:${graphId == null ? 'overall' : point.resource_version}`;
};

function TrendChart({items, graphId}) {
    const validTimes = items.map((item) => new Date(item.snapshot_at).getTime()).filter(Number.isFinite);
    if (!validTimes.length) return <p className="als-empty">نقطهٔ زمانی معتبری برای رسم نمودار وجود ندارد.</p>;
    const min = Math.min(...validTimes);
    const max = Math.max(...validTimes);
    const x = (point, index) => max === min ? LEFT + chartWidth / 2
        : LEFT + ((new Date(point.snapshot_at).getTime() - min) / (max - min)) * chartWidth;
    const y = (value) => TOP + (1 - value) * chartHeight;
    const makeSegments = (field) => {
        const result = [];
        let current = [];
        let previousKey = null;
        items.forEach((point, index) => {
            const value = ratio(point[field], field === 'mastery' ? point.status : undefined);
            const key = segmentKey(point, graphId, index);
            if (value === null || !Number.isFinite(new Date(point.snapshot_at).getTime())) {
                if (current.length) result.push(current);
                current = []; previousKey = null; return;
            }
            if (current.length && key !== previousKey) { result.push(current); current = []; }
            current.push({x: x(point, index), y: y(value), point});
            previousKey = key;
        });
        if (current.length) result.push(current);
        return result;
    };
    return <div className="als-trend-chart"><div className="als-trend-legend"><span><i className="mastery"/> تسلط</span><span><i className="confidence"/> اطمینان</span></div>
        <svg viewBox={`0 0 ${WIDTH} ${HEIGHT}`} role="img" aria-label="روند تسلط و اطمینان برآورد در زمان؛ جزئیات هر نقطه در فهرست زیر نمودار آمده است">
            {[0, .25, .5, .75, 1].map((value) => <g key={value}><line x1={LEFT} x2={WIDTH - RIGHT} y1={y(value)} y2={y(value)} className="als-gridline"/>
                <text x={LEFT - 8} y={y(value) + 4} textAnchor="end">{Math.round(value * 100)}</text></g>)}
            {['mastery', 'confidence'].map((field) => makeSegments(field).map((segment, index) => <g key={`${field}:${index}`} className={`als-trend-${field}`}>
                {segment.length > 1 && <polyline points={segment.map((point) => `${point.x},${point.y}`).join(' ')}/>}
                {segment.map((point, pointIndex) => <circle key={pointIndex} cx={point.x} cy={point.y} r={items.length > 30 ? 2.5 : 4}><title>{dateText(point.point.snapshot_at)} · {field === 'mastery' ? 'تسلط' : 'اطمینان'} {percent(point.point[field])}</title></circle>)}
            </g>))}
            <text x={LEFT} y={HEIGHT - 8} textAnchor="start">{dateText(new Date(min).toISOString())}</text>
            <text x={WIDTH - RIGHT} y={HEIGHT - 8} textAnchor="end">{dateText(new Date(max).toISOString())}</text>
        </svg>
        <p className="als-hint">گسست خط یعنی نسخهٔ منبع یا الگوریتم تغییر کرده، یا دادهٔ آن نقطه برای مقایسه کافی نیست. هر نقطه بعد از یک پاسخ نمره‌خوردهٔ یکتا ثبت شده است.</p>
    </div>;
}

export default function LearnerHistory({learnerId, graphId}) {
    const [period, setPeriod] = useState('30');
    const [state, setState] = useState({items: [], nextCursor: null, domain: null, loading: true, loadingMore: false, error: null});
    const requestId = useRef(0);
    const [retry, setRetry] = useState(0);
    const filters = useRef({});
    useEffect(() => {
        const id = ++requestId.current;
        let active = true;
        const to = new Date();
        const from = period === 'all' ? undefined : new Date(to.getTime() - Number(period) * 86400000).toISOString();
        filters.current = {graphId, from, to: period === 'all' ? undefined : to.toISOString(), limit: 100};
        setState({items: [], nextCursor: null, domain: null, loading: true, loadingMore: false, error: null});
        adminLearnerStateApi.history(learnerId, filters.current)
            .then((data) => { if (active && requestId.current === id) setState({items: Array.isArray(data.items) ? data.items : [], nextCursor: data.next_cursor || null, domain: data.domain || null, loading: false, loadingMore: false, error: null}); })
            .catch((error) => { if (active && requestId.current === id) setState({items: [], nextCursor: null, domain: null, loading: false, loadingMore: false, error}); });
        return () => { active = false; };
    }, [learnerId, graphId, period, retry]);
    const loadMore = () => {
        if (!state.nextCursor || state.loadingMore) return;
        const id = requestId.current;
        setState((current) => ({...current, loadingMore: true}));
        adminLearnerStateApi.history(learnerId, {...filters.current, cursor: state.nextCursor})
            .then((data) => { if (requestId.current === id) setState((current) => ({...current, items: [...current.items, ...(Array.isArray(data.items) ? data.items : [])], nextCursor: data.next_cursor || null, loadingMore: false, error: null})); })
            .catch((error) => { if (requestId.current === id) setState((current) => ({...current, loadingMore: false, error})); });
    };
    const recent = state.items.slice(-8).reverse();
    return <section className="als-card" aria-labelledby={`als-history-${graphId ?? 'all'}`}>
        <div className="als-section-head"><div><span className="als-eyebrow">پس از پاسخ‌های نمره‌خورده</span><h2 id={`als-history-${graphId ?? 'all'}`}>روند یادگیری</h2></div>
            <span>{state.items.length.toLocaleString('fa-IR')} نقطهٔ دریافت‌شده{state.nextCursor ? ' · ادامه دارد' : ''}</span></div>
        <div className="als-periods" role="group" aria-label="بازهٔ زمانی روند">{[['30', '۳۰ روز'], ['90', '۹۰ روز'], ['all', 'همه']].map(([value, label]) => <button key={value} type="button" className={period === value ? 'active' : ''} aria-pressed={period === value} onClick={() => setPeriod(value)}>{label}</button>)}</div>
        {state.loading && <p role="status">در حال دریافت تاریخچه...</p>}
        {state.error && <div className="als-error" role="alert"><p>{getErrorMessage(state.error)}</p><button type="button" onClick={() => setRetry((value) => value + 1)}>تلاش دوباره</button></div>}
        {!state.loading && !state.items.length && !state.error && <p className="als-empty">در این بازه پاسخ نمره‌خورده‌ای برای رسم روند ثبت نشده است. بازهٔ دیگری را انتخاب کنید.</p>}
        {!!state.items.length && <><TrendChart items={state.items} graphId={graphId}/>
            {state.nextCursor && <p className="als-hint" role="status">نمودار هنوز همهٔ نقطه‌های این بازه را ندارد. برای دیدن پاسخ‌های جدیدتر، «دریافت نقطه‌های بیشتر» را بزنید.</p>}
            <details className="als-details"><summary>آخرین نقطه‌های دریافت‌شده</summary><ol className="als-history-events">{recent.map((point, index) => <li key={`${point.snapshot_at}:${index}`}><strong>{dateText(point.snapshot_at)}</strong><span>تسلط {percent(point.mastery, point.status)} · اطمینان {percent(point.confidence)} · {Number(point.evidence_count).toLocaleString('fa-IR')} پاسخ یکتا</span></li>)}</ol></details></>}
        {state.nextCursor && <button type="button" disabled={state.loadingMore} onClick={loadMore}>{state.loadingMore ? 'در حال دریافت...' : 'دریافت نقطه‌های بیشتر'}</button>}
    </section>;
}
